import { useId, useState, type ReactNode } from 'react'
import { Alert, Button, Modal, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { ApiRequestError } from '@/lib/httpClient'
import { useClientRefreshHistory, useTriggerClientRefreshMutation } from '@/features/platform/hooks'
import type { ClientRefreshBatch, ClientRefreshStatus } from '@/features/platform/types'
import { formatPlatformDateTime } from '@/features/platform/utils'

type Props = {
  open: boolean
  clientId: string
  onClose: () => void
  addToast: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

type PendingTrigger = {
  triggeredAt: string
  knownBatchIds: Set<string>
}

const inProgressHint = 'A refresh is already in progress'
const pendingTriggerWindowMs = 5 * 60 * 1000

const renderTime = (value: string | null) => (value ? formatPlatformDateTime(value) : '—')

const statusTag: Record<ClientRefreshStatus, { color: string; label: string; icon: ReactNode }> = {
  inProgress: { color: 'cyan', label: 'In Progress', icon: <SyncOutlined spin /> },
  completed: { color: 'green', label: 'Completed', icon: <CheckCircleOutlined /> },
  failed: { color: 'red', label: 'Failed', icon: <CloseCircleOutlined /> },
}

const columns: ColumnsType<ClientRefreshBatch> = [
  {
    title: 'Triggered At',
    dataIndex: 'triggeredAt',
    key: 'triggeredAt',
    render: renderTime,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (value: ClientRefreshBatch['status']) => {
      if (typeof value === 'object') return <Tag>{value.raw || 'Unknown'}</Tag>
      const tag = statusTag[value]
      return (
        <Tag color={tag.color} icon={tag.icon}>
          {tag.label}
        </Tag>
      )
    },
  },
  {
    title: 'Finished At',
    dataIndex: 'finishedAt',
    key: 'finishedAt',
    render: renderTime,
  },
]

export function ClientRefreshModal({ open, clientId, onClose, addToast }: Props) {
  const hintId = useId()
  const history = useClientRefreshHistory(clientId, { enabled: open })
  const triggerMutation = useTriggerClientRefreshMutation(clientId)
  const [pendingTrigger, setPendingTrigger] = useState<PendingTrigger | null>(null)

  const batches = history.data ?? []
  // The trigger only queues the refresh, so its batch can be missing from the next history
  // fetch. A stand-in row keeps Start disabled until then, unless the message looks lost.
  const awaitingBatch =
    pendingTrigger != null &&
    history.dataUpdatedAt - Date.parse(pendingTrigger.triggeredAt) < pendingTriggerWindowMs &&
    batches.every((batch) => pendingTrigger.knownBatchIds.has(batch.batchId))
  // Cleared during render, not in an effect, so the stand-in can't return if the new batch drops out of a later fetch.
  if (pendingTrigger && !awaitingBatch) setPendingTrigger(null)
  const rows: ClientRefreshBatch[] =
    awaitingBatch && pendingTrigger
      ? [{ batchId: 'pending', triggeredAt: pendingTrigger.triggeredAt, status: 'inProgress', finishedAt: null }, ...batches]
      : batches
  const latest = rows[0]
  const latestInProgress = latest?.status === 'inProgress'
  const latestFailed = latest?.status === 'failed'
  const startDisabledReason = history.isFetching
    ? 'Loading refresh history'
    : history.isError
      ? 'Reload the history first'
      : latestInProgress
        ? inProgressHint
        : undefined

  const startRefresh = async () => {
    const knownBatchIds = new Set(batches.map((batch) => batch.batchId))
    try {
      await triggerMutation.mutateAsync()
      setPendingTrigger({ triggeredAt: new Date().toISOString(), knownBatchIds })
      addToast('Client refresh started', 'success')
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 409) {
        addToast(e.message || inProgressHint, 'warning')
        return
      }
      const message = e instanceof Error ? e.message : 'Unable to start client refresh'
      addToast(message, 'error')
    }
  }

  const confirmStart = () => {
    Modal.confirm({
      title: 'Start client refresh?',
      content: 'This refreshes data from every platform connected to this client.',
      okText: 'Start refresh',
      cancelText: 'Cancel',
      onOk: () => {
        void startRefresh()
      },
    })
  }

  return (
    <Modal
      open={open}
      title="Client Refresh"
      width={680}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <p className="text-sm text-gray-500 mb-4">
        Review past refreshes for this client before starting a new one.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <span className="text-sm font-semibold text-gray-700">Refresh history</span>
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip title="Reload">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Reload refresh history"
              loading={history.isFetching}
              onClick={() => void history.refetch()}
            />
          </Tooltip>
          <Tooltip title={startDisabledReason}>
            <span className="inline-block">
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={triggerMutation.isPending}
                disabled={startDisabledReason !== undefined}
                aria-describedby={latestInProgress ? hintId : undefined}
                onClick={confirmStart}
              >
                Start Client Refresh
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {latestInProgress && (
        <p id={hintId} className="text-xs text-gray-500 text-right -mt-1 mb-3">
          {inProgressHint}
        </p>
      )}

      {history.isError && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message="Couldn't load refresh history"
          description={
            history.error instanceof Error
              ? `${history.error.message.replace(/\.$/, '')}. Click Reload to try again.`
              : 'Click Reload to try again.'
          }
        />
      )}

      {!history.isError && latestFailed && (
        <Alert
          className="mb-3"
          type="error"
          showIcon
          message="Refresh Failed. Kindly reach out to the Devs or BA team"
        />
      )}

      <Table<ClientRefreshBatch>
        rowKey="batchId"
        size="middle"
        columns={columns}
        dataSource={history.isError ? [] : rows}
        loading={history.isFetching}
        pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        locale={{
          emptyText: history.isFetching
            ? null
            : history.isError
              ? 'Refresh history unavailable'
              : 'No client refreshes yet. Click "Start Client Refresh" to run the first one.',
        }}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  )
}
